<template>
  <div>
    <q-dialog :value="value" @input="$emit('input', $event)" persistent>
      <q-card style="min-width: 1100px">
        <q-card-section class="q-pb-sm">
          <div class="text-h6">{{ $t('commission_rules') }}</div>
          <div class="text-caption text-grey-8" v-if="comission">{{ comissionSummary }}</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-banner dense class="bg-blue-1 text-grey-9 q-mb-md">
            <template v-slot:avatar>
              <q-icon name="mdi-information-outline" color="primary" />
            </template>
            {{ $t('commission_rules_hint') }}
          </q-banner>

          <q-table
            flat
            bordered
            :data="rules"
            :columns="columns"
            row-key="id"
            :pagination="pagination"
            hide-pagination
            :no-data-label="$t('no_commission_rules')"
          >
            <template v-slot:top-left>
              <q-btn
                color="positive"
                no-caps
                unelevated
                icon="mdi-plus"
                :label="$t('new_rule')"
                @click="openForm()"
              />
            </template>

            <template v-slot:body-cell-priority="props">
              <q-td :props="props">
                <div class="row items-center no-wrap">
                  <q-btn
                    dense
                    flat
                    round
                    size="sm"
                    icon="mdi-arrow-up"
                    color="primary"
                    :disable="props.rowIndex === 0 || reordering"
                    @click="move(props.rowIndex, -1)"
                  >
                    <q-tooltip>{{ $t('move_up') }}</q-tooltip>
                  </q-btn>
                  <q-btn
                    dense
                    flat
                    round
                    size="sm"
                    icon="mdi-arrow-down"
                    color="primary"
                    :disable="props.rowIndex === rules.length - 1 || reordering"
                    @click="move(props.rowIndex, 1)"
                  >
                    <q-tooltip>{{ $t('move_down') }}</q-tooltip>
                  </q-btn>
                  <span class="q-ml-sm text-weight-medium">{{ props.row.priority }}</span>
                </div>
              </q-td>
            </template>

            <template v-slot:body-cell-criteria="props">
              <q-td :props="props">
                <div v-for="criterion in props.row.criteria" :key="criterion.id" class="text-caption">
                  {{ criterionLabel(criterion) }}
                </div>
              </q-td>
            </template>

            <template v-slot:body-cell-value="props">
              <q-td :props="props">{{ ruleValueLabel(props.row) }}</q-td>
            </template>

            <template v-slot:body-cell-action="props">
              <q-td :props="props">
                <q-btn unelevated round flat dense icon="mdi-pencil" color="primary" @click="openForm(props.row)">
                  <q-tooltip>{{ $t('edit') }}</q-tooltip>
                </q-btn>
                <q-btn unelevated round flat dense icon="mdi-delete" color="negative" @click="confirmDelete(props.row)">
                  <q-tooltip>{{ $t('delete') }}</q-tooltip>
                </q-btn>
              </q-td>
            </template>
          </q-table>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat no-caps :label="$t('close')" @click="$emit('input', false)" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="formDialog" persistent>
      <q-card style="min-width: 1400px; max-width: 96vw">
        <q-form @submit.prevent="saveRule">
          <q-card-section>
            <div class="text-h6">{{ form.id ? $t('edit_rule') : $t('new_rule') }}</div>
          </q-card-section>

          <q-card-section class="q-pt-none">
            <div class="row q-col-gutter-md">
              <div class="col-9">
            <div class="row q-col-gutter-sm">
              <div class="col-6">
                <q-input
                  outlined
                  dense
                  v-model="form.name"
                  type="text"
                  :label="$t('rule_name') + '*'"
                  lazy-rules
                  :rules="[isRequired]"
                />
              </div>

              <div class="col-3">
                <q-select
                  outlined
                  dense
                  v-model="form.format"
                  :options="formatOptions"
                  emit-value
                  map-options
                  :label="$t('negotiation_type') + '*'"
                  lazy-rules
                  :rules="[isRequired]"
                />
              </div>

              <div class="col-3">
                <q-input
                  outlined
                  dense
                  v-model="form.value"
                  type="number"
                  step="0.0001"
                  :suffix="form.format === 'percentage' ? '%' : currencyLabel"
                  :label="$t('value') + '*'"
                  lazy-rules
                  :rules="[isFilledValue]"
                />
              </div>
            </div>

            <q-separator spaced />

            <div class="row items-center q-mb-sm">
              <div class="text-subtitle2">{{ $t('criteria') }}</div>
              <q-space />
              <q-btn
                dense
                unelevated
                no-caps
                color="primary"
                icon="mdi-plus"
                :label="$t('add_criterion')"
                @click="addCriterion"
              />
            </div>

            <div class="text-caption text-grey-8 q-mb-sm">{{ $t('criteria_hint') }}</div>

            <div
              v-for="(criterion, index) in form.criteria"
              :key="index"
              class="row q-col-gutter-sm q-mb-sm items-start"
            >
              <div class="col-2">
                <q-input
                  outlined
                  dense
                  v-model="criterion.name"
                  type="text"
                  :label="$t('criterion_name') + '*'"
                  lazy-rules
                  :rules="[isRequired]"
                />
              </div>

              <div class="col-2">
                <q-select
                  outlined
                  dense
                  v-model="criterion.parameter"
                  :options="parameterOptions"
                  emit-value
                  map-options
                  :label="$t('tracking_parameter') + '*'"
                  lazy-rules
                  :rules="[isRequired]"
                />
              </div>

              <div class="col-2">
                <q-select
                  outlined
                  dense
                  v-model="criterion.data_type"
                  :options="dataTypeOptions"
                  emit-value
                  map-options
                  :label="$t('data_type') + '*'"
                  lazy-rules
                  :rules="[isRequired]"
                  @input="onDataTypeChange(criterion)"
                />
              </div>

              <div class="col-2">
                <q-select
                  outlined
                  dense
                  v-model="criterion.operator"
                  :options="operatorOptions(criterion.data_type, criterion.operator)"
                  emit-value
                  map-options
                  :label="$t('operator') + '*'"
                  lazy-rules
                  :rules="[isRequired]"
                  @input="onOperatorChange(criterion)"
                />
              </div>

              <div class="col-3">
                <q-select
                  v-if="criterion.operator === 'in'"
                  outlined
                  dense
                  v-model="criterion.values"
                  multiple
                  use-input
                  use-chips
                  hide-dropdown-icon
                  new-value-mode="add-unique"
                  :options="[]"
                  :label="$t('values') + '*'"
                  :hint="$t('in_values_hint')"
                  lazy-rules
                  :rules="[isFilledList]"
                />

                <div v-else-if="criterion.operator === 'between'" class="row q-col-gutter-xs">
                  <div class="col-6">
                    <q-input
                      outlined
                      dense
                      v-model="criterion.value_min"
                      type="number"
                      :label="$t('min_value') + '*'"
                      lazy-rules
                      :rules="[isFilledValue]"
                    />
                  </div>
                  <div class="col-6">
                    <q-input
                      outlined
                      dense
                      v-model="criterion.value_max"
                      type="number"
                      :label="$t('max_value') + '*'"
                      lazy-rules
                      :rules="[isFilledValue, (val) => isValidRange(criterion, val)]"
                    />
                  </div>
                </div>

                <q-input
                  v-else
                  outlined
                  dense
                  v-model="criterion.value"
                  :type="criterion.data_type === 'number' ? 'number' : 'text'"
                  :label="$t('value') + '*'"
                  :hint="criterion.operator === 'like' ? $t('like_hint') : ''"
                  lazy-rules
                  :rules="[isFilledValue]"
                />
              </div>

              <div class="col-1">
                <q-btn
                  dense
                  color="negative"
                  icon="mdi-close"
                  :disable="form.criteria.length === 1"
                  @click="removeCriterion(index)"
                >
                  <q-tooltip>{{ $t('remove_criterion') }}</q-tooltip>
                </q-btn>
              </div>
            </div>
              </div>

              <div class="col-3">
                <q-card flat bordered class="bg-grey-1">
                  <q-card-section class="q-pb-none">
                    <div class="text-subtitle2">{{ $t('rules_reference') }}</div>
                  </q-card-section>

                  <q-card-section>
                    <div class="text-caption text-weight-medium q-mb-xs">{{ $t('how_it_works') }}</div>
                    <ol class="q-my-none q-pl-md text-caption text-grey-8">
                      <li>{{ $t('how_it_works_step1') }}</li>
                      <li>{{ $t('how_it_works_step2') }}</li>
                      <li>{{ $t('how_it_works_step3') }}</li>
                    </ol>
                  </q-card-section>

                  <q-separator />

                  <q-card-section>
                    <div class="text-caption text-weight-medium q-mb-xs">{{ $t('available_parameters') }}</div>
                    <q-chip
                      v-for="parameter in parameterOptions"
                      :key="parameter.value"
                      dense
                      square
                      size="sm"
                      class="q-ma-none q-mr-xs q-mb-xs"
                    >
                      {{ parameter.label }}
                    </q-chip>
                  </q-card-section>

                  <q-separator />

                  <q-card-section>
                    <div class="text-caption text-weight-medium q-mb-xs">{{ $t('supported_operators') }}</div>

                    <div class="text-caption text-grey-8 q-mt-sm q-mb-xs">{{ $t('data_type_text') }}</div>
                    <q-chip
                      v-for="operator in operatorOptions('text')"
                      :key="'text-' + operator.value"
                      dense
                      square
                      size="sm"
                      class="q-ma-none q-mr-xs q-mb-xs"
                    >
                      {{ operator.label }}
                    </q-chip>

                    <div class="text-caption text-grey-8 q-mt-sm q-mb-xs">{{ $t('data_type_number') }}</div>
                    <q-chip
                      v-for="operator in operatorOptions('number')"
                      :key="'number-' + operator.value"
                      dense
                      square
                      size="sm"
                      class="q-ma-none q-mr-xs q-mb-xs"
                    >
                      {{ operator.label }}
                    </q-chip>
                  </q-card-section>
                </q-card>
              </div>
            </div>
          </q-card-section>

          <q-card-actions align="right">
            <q-btn flat no-caps :label="$t('cancel')" @click="formDialog = false" />
            <q-btn type="submit" unelevated no-caps color="positive" :label="$t('save')" class="q-px-md" />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import AdvertiserService from '@/services/AdvertiserService';

export default {
  name: 'ComissionRules',
  mixins: [AdvertiserService],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    comission: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      rules: [],
      options: {
        parameters: [],
        formats: [],
        data_types: [],
        operators: {}
      },
      formDialog: false,
      reordering: false,
      form: this.emptyForm(),
      pagination: {
        rowsPerPage: 0
      },
      columns: [
        {
          name: 'priority',
          align: 'left',
          label: this.$t('priority'),
          field: 'priority',
          style: 'width: 140px'
        },
        {
          name: 'name',
          align: 'left',
          label: this.$t('rule_name'),
          field: 'name'
        },
        {
          name: 'criteria',
          align: 'left',
          label: this.$t('criteria'),
          field: 'criteria'
        },
        {
          name: 'value',
          align: 'left',
          label: this.$t('value'),
          field: 'value',
          style: 'width: 140px'
        },
        {
          name: 'action',
          align: 'right',
          label: this.$t('actions'),
          field: 'id',
          style: 'width: 110px'
        }
      ]
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(opened) {
        if (opened) this.load();
      }
    }
  },
  computed: {
    currencyLabel() {
      return this.comission && this.comission.currency ? this.comission.currency : 'R$';
    },

    comissionSummary() {
      const type = (this.comission.type || '').toUpperCase();
      const base = this.comission.format === 'percentage' ? `${this.comission.value}%` : `${this.currencyLabel} ${this.comission.value}`;

      return `${type} - ${this.comission.category} | ${this.$t('default_commission')}: ${base}`;
    },

    formatOptions() {
      return (this.options.formats || []).map((format) => ({
        label: format === 'percentage' ? this.$t('percentage') : this.$t('value'),
        value: format
      }));
    },

    parameterOptions() {
      return (this.options.parameters || []).map((parameter) => ({ label: parameter, value: parameter }));
    },

    dataTypeOptions() {
      return (this.options.data_types || []).map((dataType) => ({
        label: this.$t(`data_type_${dataType}`),
        value: dataType
      }));
    }
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: null,
        format: 'value',
        value: null,
        criteria: [this.emptyCriterion()]
      };
    },

    emptyCriterion() {
      return {
        name: null,
        parameter: null,
        data_type: 'text',
        operator: null,
        value: null,
        values: [],
        value_min: null,
        value_max: null
      };
    },

    operatorOptions(dataType, current) {
      const operators = (this.options.operators && this.options.operators[dataType]) || [];

      // Regra antiga pode ter operador que saiu da lista oferecida, como o like.
      // Sem mantê-lo aqui o select abriria vazio na edição e a regra seria salva
      // sem operador, silenciosamente.
      const available = current && !operators.includes(current) ? operators.concat([current]) : operators;

      return available.map((operator) => ({ label: this.$t(`operator_${operator}`), value: operator }));
    },

    isFilledList(list) {
      return (Array.isArray(list) && list.length > 0) || this.$t('is_required');
    },

    /**
     * Zero é um valor válido de comissão e de faixa, então o obrigatório global
     * (baseado em !!val) não serve para os campos numéricos.
     */
    isFilledValue(val) {
      return (val !== null && val !== undefined && val !== '') || this.$t('is_required');
    },

    isValidRange(criterion, max) {
      const min = criterion.value_min;
      if (this.isFilledValue(min) !== true || this.isFilledValue(max) !== true) return true;

      return Number(min) <= Number(max) || this.$t('min_value_greater_than_max');
    },

    async load() {
      this.onLoading(true);
      try {
        await Promise.all([this.loadOptions(), this.loadRules()]);
      } finally {
        this.onLoading(false);
      }
    },

    async loadOptions() {
      try {
        const { data, status } = await this.getComissionRuleOptions();
        if (status === 200) this.options = data;
      } catch (error) {
        this.errorNotify(error);
      }
    },

    async loadRules() {
      try {
        const { data, status } = await this.listComissionRules(this.comission.id);
        if (status === 200) this.rules = data.rules || [];
      } catch (error) {
        this.errorNotify(error);
      }
    },

    openForm(rule) {
      this.form = rule ? this.ruleToForm(rule) : this.emptyForm();
      this.formDialog = true;
    },

    ruleToForm(rule) {
      return {
        id: rule.id,
        name: rule.name,
        format: rule.format,
        value: rule.value,
        criteria: rule.criteria.map((criterion) => ({
          name: criterion.name,
          parameter: criterion.parameter,
          data_type: criterion.data_type,
          operator: criterion.operator,
          value: criterion.value,
          values: criterion.values || [],
          value_min: criterion.value_min,
          value_max: criterion.value_max
        }))
      };
    },

    addCriterion() {
      this.form.criteria.push(this.emptyCriterion());
    },

    removeCriterion(index) {
      this.form.criteria.splice(index, 1);
    },

    /**
     * Operador e valor dependem do tipo de dado, então voltam ao estado inicial quando ele muda.
     */
    onDataTypeChange(criterion) {
      criterion.operator = null;
      this.onOperatorChange(criterion);
    },

    onOperatorChange(criterion) {
      criterion.value = null;
      criterion.values = [];
      criterion.value_min = null;
      criterion.value_max = null;
    },

    criteriaPayload() {
      return this.form.criteria.map((criterion) => ({
        name: criterion.name,
        parameter: criterion.parameter,
        data_type: criterion.data_type,
        operator: criterion.operator,
        value: criterion.operator === 'in' ? criterion.values : criterion.value,
        value_min: criterion.value_min,
        value_max: criterion.value_max
      }));
    },

    async saveRule() {
      this.onLoading(true);
      try {
        const payload = {
          name: this.form.name,
          format: this.form.format,
          value: this.form.value,
          criteria: this.criteriaPayload()
        };

        if (this.form.id) {
          await this.editComissionRule(this.form.id, payload);
        } else {
          await this.createComissionRule({ ...payload, comission_id: this.comission.id });
        }

        this.successNotify(this.$t('rule_saved'));
        this.formDialog = false;
        await this.loadRules();
      } catch (error) {
        this.errorNotify(error);
      } finally {
        this.onLoading(false);
      }
    },

    confirmDelete(rule) {
      this.showDialog(this.$t('attention'), this.$t('confirm_delete_rule', { name: rule.name }), () =>
        this.removeRule(rule.id)
      );
    },

    async removeRule(ruleId) {
      this.onLoading(true);
      try {
        await this.deleteComissionRule(ruleId);
        this.successNotify(this.$t('rule_deleted'));
        await this.loadRules();
      } catch (error) {
        this.errorNotify(error);
      } finally {
        this.onLoading(false);
      }
    },

    async move(index, direction) {
      const reordered = this.rules.slice();
      const [moved] = reordered.splice(index, 1);
      reordered.splice(index + direction, 0, moved);

      this.reordering = true;
      try {
        const { data, status } = await this.reorderComissionRules(
          this.comission.id,
          reordered.map((rule) => rule.id)
        );

        if (status === 200) this.rules = data.rules || [];
      } catch (error) {
        this.errorNotify(error);
      } finally {
        this.reordering = false;
      }
    },

    criterionLabel(criterion) {
      const operator = this.$t(`operator_${criterion.operator}`);

      return `${criterion.name} (${criterion.parameter}) ${operator} ${this.criterionValueLabel(criterion)}`;
    },

    criterionValueLabel(criterion) {
      if (criterion.operator === 'in') return (criterion.values || []).join(', ');
      if (criterion.operator === 'between') return `${criterion.value_min} - ${criterion.value_max}`;

      return criterion.value;
    },

    ruleValueLabel(rule) {
      return rule.format === 'percentage' ? `${rule.value}%` : `${this.currencyLabel} ${rule.value}`;
    }
  }
};
</script>
